import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '全部专题 | 瓜田黑料',
    description: '瓜田黑料全部专题列表，可按事件复盘、舆论观察、行业生态、理性吃瓜、旧帖考古五个方向筛选。',
    url: 'https://changshazy.cn/topics',
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://changshazy.cn/' },
      { '@type': 'ListItem', position: 2, name: '全部专题', item: 'https://changshazy.cn/topics' },
    ],
  },
];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const keyword = searchParams.get('q') ?? '';

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    return articles
      .filter((item) => (activeCategory === 'all' ? true : item.categoryId === activeCategory))
      .filter((item) => {
        if (!query) return true;
        return (
          item.title.toLowerCase().includes(query) ||
          item.excerpt.toLowerCase().includes(query) ||
          item.tags.some((tag) => tag.toLowerCase().includes(query))
        );
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCategory, keyword]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === 'all') params.delete('category');
      else params.set('category', next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete('q');
      else params.set('q', next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const currentCategory = categories.find((item) => item.id === activeCategory);

  return (
    <SiteLayout>
      <Seo
        title="全部专题 | 瓜田黑料娱乐热点复盘合集"
        description="浏览瓜田黑料的全部专题复盘，按事件复盘、舆论观察、行业生态、理性吃瓜、旧帖考古五个方向筛选，也可用关键词直接搜索你关注的热点。"
        keywords="瓜田黑料专题,娱乐热点合集,舆论观察文章,时间线复盘列表"
        path="/topics"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">全部专题</span>
          </nav>

          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            全部专题
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里汇集了站内所有时间线复盘与观察文章。先用分类缩小范围，再用关键词精确到某个话题、机构或环节。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
          <div className="flex flex-col gap-5">
            <label className="flex w-full items-center gap-3 rounded-lg border border-background-300 bg-background-100/60 px-4 py-3 focus-within:border-primary-400">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => updateParams({ q: event.target.value })}
                placeholder="搜索事件、环节或标签，例如：剪辑、公关、时间线"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索专题"
              />
              {keyword ? (
                <button
                  type="button"
                  onClick={() => updateParams({ q: '' })}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-background-200 text-foreground-600"
                  aria-label="清空搜索"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              ) : null}
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ category: 'all' })}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                }`}
              >
                全部分类
              </button>
              {categories.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => updateParams({ category: item.id })}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    activeCategory === item.id
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
              <span className="whitespace-nowrap">
                共 <strong className="font-semibold text-primary-700">{filtered.length}</strong> 篇
                {currentCategory ? ` · ${currentCategory.name}` : ''}
              </span>
              {keyword ? (
                <span className="whitespace-nowrap text-foreground-500">关键词「{keyword}」</span>
              ) : null}
              {(keyword || activeCategory !== 'all') ? (
                <button
                  type="button"
                  onClick={() => setSearchParams({}, { replace: true })}
                  className="cursor-pointer whitespace-nowrap text-primary-700 hover:underline"
                >
                  重置筛选
                </button>
              ) : null}
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-600">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-950">没有找到匹配的专题</h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                换个关键词试试，或者直接提交你想看的方向，我们会优先整理。
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setSearchParams({}, { replace: true })}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                >
                  重置筛选
                </button>
                <Link
                  to="/contact"
                  className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  提交线索
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}